import { useCallback, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Modal, Stack } from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { getDeviceTypeMessage } from '../delete-device/utils/utils'
import { DarkEditAlarmForm } from '../edit-alarm/dark/DarkEditAlarmForm'
import { DarkEditCameraForm } from '../edit-camera/dark/DarkEditCameraForm'
import { DarkEditDoorForm } from '../edit-door/dark/DarkEditDoorForm'
import { EditDeviceFieldsI } from '../types/types'
import { DarkAssociateDevices } from './DarkAssociateDevices'

interface DarkEditDeviceModalIProps {
  onClose: () => void
  isOpen: boolean
  deviceData: EditDeviceFieldsI
  onSave?: () => void
  /** Overlay z-index. Required when the modal is opened from a drawer, which
   * outranks the dark Modal's 1000 default. */
  zIndex?: number
}

type LoadingStates = {
  [key in DeviceType]?: boolean
}

export const DarkEditDeviceModal = ({
  onClose,
  isOpen,
  deviceData,
  onSave,
  zIndex,
}: DarkEditDeviceModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const id = deviceData?.id
  const type = deviceData?.type
  const device = getDeviceTypeMessage(type)
  const [shouldRequireAuthentication, setShouldRequireAuthentication] =
    useState(true)
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({})

  const isAnySectionLoading = Object.values(loadingStates).some(Boolean)
  const shouldShowNoCameraAuthenticationAlert =
    !shouldRequireAuthentication && type === DeviceType.Camera

  const updateLoadingState = useCallback(
    (loadingState: boolean, deviceType: DeviceType) => {
      setLoadingStates((previous) => ({
        ...previous,
        [deviceType]: loadingState,
      }))
    },
    []
  )
  const handleAlarmLoadingChange = useCallback(
    (loading: boolean) => updateLoadingState(loading, DeviceType.Alarm),
    [updateLoadingState]
  )
  const handleCameraLoadingChange = useCallback(
    (loading: boolean) => updateLoadingState(loading, DeviceType.Camera),
    [updateLoadingState]
  )
  const handleDoorLoadingChange = useCallback(
    (loading: boolean) => updateLoadingState(loading, DeviceType.Door),
    [updateLoadingState]
  )

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      // The edit form owns the interaction until the user saves or closes it —
      // a stray overlay click must not discard in-progress edits. (ENG-33365)
      closeOnOverlayClick={false}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveHidden
      maxW='1143px'
      onClose={onClose}
      title={tVar(t, 'devices:crud_edit_title', { device })}
      zIndex={zIndex}
    >
      <Box id='editCameraModalBody'>
        <Stack gap='16px'>
          {shouldShowNoCameraAuthenticationAlert && (
            <Alert
              colorScheme={colorScheme}
              description={t('devices:crud_unauthenticated_camera_alert')}
              variant='warning'
            />
          )}

          {type === DeviceType.Alarm && (
            <DarkEditAlarmForm
              colorScheme={colorScheme}
              deviceId={id}
              onLoadingChange={handleAlarmLoadingChange}
            />
          )}
          {type === DeviceType.Camera && (
            <DarkEditCameraForm
              colorScheme={colorScheme}
              deviceId={id}
              onLoadingChange={handleCameraLoadingChange}
              onRequiresAuthenticationChange={setShouldRequireAuthentication}
              shouldRequireAuthentication={shouldRequireAuthentication}
            />
          )}
          {type === DeviceType.Door && (
            <DarkEditDoorForm
              colorScheme={colorScheme}
              deviceId={id}
              onLoadingChange={handleDoorLoadingChange}
              onSave={onSave}
            />
          )}

          <DarkAssociateDevices
            colorScheme={colorScheme}
            deviceData={deviceData}
            isOtherSectionLoading={isAnySectionLoading}
            onSave={onSave}
            // Nested modals must outrank this modal's own overlay
            overlayZIndex={zIndex !== undefined ? zIndex + 1 : undefined}
          />
        </Stack>
      </Box>
    </Modal>
  )
}
